import { createContext, useContext, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { DIAGNOSTICS_ENDPOINTS, DIAGNOSTICS_TIMEOUTS } from './config/endpoints';
import { checkReachability } from './services/ReachabilityService';
import { createEmptyReport } from './types';

const DiagnosticsContext = createContext(null);

const postTelemetry = (url, payload) => {
  if (!url) return;
  fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch(() => undefined);
};

export const DiagnosticsProvider = ({ children }) => {
  const currentUser = useSelector(getCurrentUser);
  const [report, setReport] = useState(createEmptyReport);
  const [isRunning, setIsRunning] = useState(false);

  const runTest = useCallback(async () => {
    setIsRunning(true);
    try {
      const reachability = await checkReachability({
        endpoints: DIAGNOSTICS_ENDPOINTS,
        timeoutMs: DIAGNOSTICS_TIMEOUTS.request,
      });

      const nextReport = {
        schemaVersion: '1.0',
        reportId: uuid(),
        generatedAt: new Date().toISOString(),
        account: {
          accountId: currentUser?.useruuid || null,
          userLabel: currentUser?.username || null,
        },
        environment: {
          browser: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        reachability,
      };

      setReport(nextReport);
      postTelemetry(DIAGNOSTICS_ENDPOINTS.reportTelemetry, nextReport);
    } finally {
      setIsRunning(false);
    }
  }, [currentUser?.useruuid, currentUser?.username]);

  const value = useMemo(
    () => ({ report, isRunning, runTest }),
    [report, isRunning, runTest]
  );

  return <DiagnosticsContext.Provider value={value}>{children}</DiagnosticsContext.Provider>;
};

DiagnosticsProvider.propTypes = {
  children: PropTypes.node,
};

export const useDiagnostics = () => {
  const context = useContext(DiagnosticsContext);
  if (!context) {
    throw new Error('useDiagnostics must be used within a DiagnosticsProvider');
  }
  return context;
};
